import { useState, useEffect } from "react";
import { Stack, DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, PrimaryButton, DefaultButton, Dialog, DialogType, DialogFooter, Spinner, SpinnerSize, Label } from "@fluentui/react";
import { getUsers, deleteUser } from "../../../api/admin/api_admin";
import { User, UsersResponse } from "../../../api/admin/models_admin";
import { AdminLabel, LinkButton, CellContent, formatDateTime, ErrorDialog, filterByKeyword, ListFilterBox } from "../components";

export const Users = () => {
    const [usersData, setUsersData] = useState<UsersResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState<boolean>(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);
    const [deleting, setDeleting] = useState<boolean>(false);
    // 絞り込み文字列
    const [userFilter, setUserFilter] = useState<string>('');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getUsers();
            if ("error" in data) {
                setError(data.error);
            } else {
                setUsersData(data);
            }
        } catch (err) {
            setError("ユーザー情報の取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (user: User) => {
        setUserToDelete(user);
        setDeleteDialogVisible(true);
    };

    const handleDeleteConfirm = async () => {
        if (!userToDelete) return;
        
        setDeleting(true);
        try {
            const result = await deleteUser(userToDelete.userid);
            if (result.success) {
                setDeleteDialogVisible(false);
                setUserToDelete(null);
                await fetchUsers(); // 再取得
            } else {
                setError(result.message || "削除に失敗しました");
                setDeleteDialogVisible(false);
            }
        } catch (err) {
            setError("削除中にエラーが発生しました");
            setDeleteDialogVisible(false);
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogVisible(false);
        setUserToDelete(null);
    };

    const canModify = (user: User): boolean => {
        if (!usersData) return false;
        const modifyFunc = usersData.functions['modify_user'];
        return modifyFunc && 
               usersData.current_user.authority_level >= modifyFunc.need_authority_level &&
               user.authority_level < 10;
    };

    const canDelete = (user: User): boolean => {
        if (!usersData) return false;
        const deleteFunc = usersData.functions['delete_user'];
        return deleteFunc && 
               usersData.current_user.authority_level >= deleteFunc.need_authority_level &&
               user.authority_level < 10;
    };

    const canAddUser = (): boolean => {
        if (!usersData) return false;
        const addFunc = usersData.functions['add_user'];
        return addFunc && usersData.current_user.authority_level >= addFunc.need_authority_level;
    };

    const canImportExport = (): boolean => {
        if (!usersData) return false;
        return usersData.current_user.authority_level >= usersData.import_export_level;
    };

    const columns: IColumn[] = [
        {
            key: 'actions',
            name: '',
            minWidth: 150,
            maxWidth: 150,
            onRender: (user: User) => (
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    {canModify(user) && (
                        <LinkButton 
                            link_to={`/menu/users/modify?user_id=${user.userid}`} 
                            text="変更" 
                            styles={{ root: { minWidth: 60 } }} 
                        />
                    )}
                    {canDelete(user) && (
                        <DefaultButton 
                            text="削除"
                            onClick={() => handleDeleteClick(user)}
                            styles={{ root: { minWidth: 60 } }}
                        />
                    )}
                </Stack>
            )
        },
        {
            key: 'userid',
            name: 'ユーザーID',
            fieldName: 'userid',
            minWidth: 100,
            maxWidth: 200,
            isResizable: true,
            onRender: (user: User) => (
                <CellContent>{user.userid}</CellContent>
            )
        },
        {
            key: 'username',
            name: 'ユーザー名',
            fieldName: 'USR_username',
            minWidth: 120,
            maxWidth: 200,
            isResizable: true,
            onRender: (user: User) => (
                <CellContent>{user.USR_username}</CellContent>
            )
        },
        {
            key: 'username_alphabet',
            name: '姓名(ローマ字)',
            minWidth: 120,
            maxWidth: 200,
            isResizable: true,
            fieldName: "USR_username_alphabet",
            onRender: (user: User) => (
                <CellContent>{user.USR_username_alphabet ?? ""}</CellContent>
            )
        },
        {
            key: 'authority_level',
            name: '権限レベル',
            fieldName: 'authority_level',
            minWidth: 80,
            maxWidth: 100,
            isResizable: true,
            onRender: (user: User) => (
                <CellContent>{user.authority_level}</CellContent>
            )
        },
        {
            key: 'groups',
            name: 'グループ',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true,
            fieldName: "usg_values",
            onRender: (user: User) => (
                <CellContent>{user.usg_values}</CellContent>
            )
        },
        {
            key: 'entry_datetime',
            name: '登録日時',
            fieldName: 'entry_datetime',
            minWidth: 150,
            maxWidth: 180,
            isResizable: true,
            onRender: (user: User) => (
                <CellContent>{formatDateTime(user.entry_datetime)}</CellContent>
            )
        },
        {
            key: 'update_datetime',
            name: '更新日時',
            fieldName: 'update_datetime',
            minWidth: 150,
            maxWidth: 180,
            isResizable: true,
            onRender: (user: User) => (
                <CellContent>{formatDateTime(user.update_datetime)}</CellContent>
            )
        }
    ];

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    const users = usersData?.users.filter(u => u.authority_level < 10) || [];
    // ID・氏名・ローマ字・グループのいずれかにキーワードが含まれる行を残す
    const filteredUsers = filterByKeyword(users, userFilter,
        u => [u.userid, u.USR_username, u.USR_username_alphabet, u.usg_values]);

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            {canImportExport() && (
                <Stack tokens={{ childrenGap: 10 }}>
                    <AdminLabel text="一括操作"/>
                    <ul>
                        <li><b>常に最新のcsvファイルをエクスポートし、上書きする形で編集してインポートを行ってください。</b></li>
                        <li>インポートはcsvとの<b>完全同期</b>です。また<b>インポートは取り消すことができない</b>ため、必ず確認画面にて最終確認をしてください。</li>
                        <li><b>エクスポートされたcsvのpassword欄は全て空白です。</b>パスワードの変更またはユーザーの新規追加の際には記入が必要となります。</li>
                        <li>グループの所属状況については、<b>1が入力されていた場合</b>にそのカラムのグループに所属していると判定します。</li>
                        <li>可能な操作は、付与されている権限レベルで可能な操作に限ります。</li>
                    </ul>
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <DefaultButton
                            text="csvエクスポート"
                            href="/api/export_users"
                            target="_blank"
                        />
                        <LinkButton link_to="/menu/users/import" text="csvインポート" />
                    </Stack>
                </Stack>
            )}

            <Stack tokens={{ childrenGap: 10 }}>
                <AdminLabel text="ユーザー一覧"/>
                <ListFilterBox
                    value={userFilter}
                    onChange={setUserFilter}
                    placeholder="ID・氏名・グループで絞り込み"
                    filteredCount={filteredUsers.length}
                    totalCount={users.length}
                />
                {filteredUsers.length === 0 ? (
                    <Label>絞り込み条件に一致するユーザーがいません。</Label>
                ) : (
                    <DetailsList
                        items={filteredUsers}
                        columns={columns}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={SelectionMode.none}
                        isHeaderVisible={true}
                    />
                )}
            </Stack>

            {canAddUser() && (
                <Stack>
                    <LinkButton link_to="/menu/add_user" text="新規登録" />
                </Stack>
            )}

            <Dialog
                hidden={!deleteDialogVisible}
                onDismiss={handleDeleteCancel}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'ユーザー削除確認',
                    subText: `ユーザー「${userToDelete?.USR_username}」(ID: ${userToDelete?.userid})を削除してもよろしいですか?`
                }}
            >
                <DialogFooter>
                    <PrimaryButton 
                        onClick={handleDeleteConfirm} 
                        text="削除" 
                        disabled={deleting}
                    />
                    <DefaultButton 
                        onClick={handleDeleteCancel} 
                        text="キャンセル"
                        disabled={deleting}
                    />
                </DialogFooter>
            </Dialog>
        </Stack>
    );
}